import { get } from 'lodash';
import findResult, { selectableResult } from '@/utils/resultsHelper';
import pagingWithFilteredResults, { presentationOptions, sortingFilters } from '@/constants/states';
import defaultStates, { states } from '@/constants/lifecycle';
import { AuthenticationState } from '@/auth/constants';
import { defaultPermissions } from '@/roles/constants';
import { isCommonsModeEnabled } from '@/auth/featureToggles';
import { findWithUUIDs } from '@/utils/uuidHelper';
import { responseError } from '@/utils/errors';
import { userCategories, userStates } from '../constants';

export const userPanels = (openPanels = [0]) => ({
  panels: openPanels,
});

export const approvalFilters = (
  approvedFlag = true,
  unapprovedFlag = true,
  archivedFlag = false,
) => ({
  approvedFlag,
  unapprovedFlag,
  archivedFlag,
});

const allUsers = (
  approvedFlag,
  unapprovedFlag,
  archivedFlag,
  orderBy = [{ column: 'name', direction: 'asc' }],
  singleSorting = true,
  display = presentationOptions.TABLE,
) => ({
  all: {
    ...pagingWithFilteredResults(),
    ...approvalFilters(approvedFlag, unapprovedFlag, archivedFlag),
    ...sortingFilters(orderBy, singleSorting),
    display,
    ...defaultStates,
    role: null,
  },
});

export const activeArchiveUsers = () => ({
  open: false,
  restoreOpen: false,
});

export const defaultRole = () => ({
  code: null,
  name: null,
  description: null,
  isLoading: false,
  approved: false,
  max_active_tokens: 0,
  max_token_length_days: 0,
  permissions: defaultPermissions,
});

const initialState = {
  initializing: false,
  initialized: false,
  idToken: null,
  accessToken: null,
  state: AuthenticationState.Unauthenticated,
  commonsMode: isCommonsModeEnabled(),
  currentUser: null,
  archiveUsers: activeArchiveUsers(),
  role: defaultRole(),
  users: {
    ...allUsers(),
    open: {
      results: [],
      roleActions: { results: [] },
    },
    archival: {
      results: [],
    },
  },
  userGroups: [],
};

export const usersResult = (result) => ({
  ...selectableResult(result),
  code: result.uuid,
  ...userStates,
});

export const userStateToLoad = ({ auth }) => {
  const items = get(auth, 'users', initialState.users);
  const allFilters = items.all || initialState.users.all;
  return {
    ...initialState,
    users: {
      ...initialState.users,
      ...allUsers(
        allFilters.approvedFlag,
        allFilters.unapprovedFlag,
        false,
        allFilters.orderBy,
        allFilters.singleSorting,
        allFilters.display,
      ),
    },
  };
};

export const userStateToSave = ({ auth }) => ({
  auth: {
    users: {
      all: {
        approvedFlag: auth.users.all.approvedFlag,
        unapprovedFlag: auth.users.all.unapprovedFlag,
        orderBy: auth.users.all.orderBy,
        singleSorting: auth.users.all.singleSorting,
        display: auth.users.all.display,
      },
    },
  },
});

export const findUsers = ({ users }, { category }) => users[category];

export const findAllUsers = ({ users }, { items }) => {
  const uuids = items.map(({ uuid }) => uuid);
  return findWithUUIDs(users, userCategories, uuids);
};

export const findUser = ({ users }, { category, code }, attribute = 'code') => (
  findResult(users, category, code, attribute)
);

export const userArchiveStatus = (action, state) => {
  const errors = state === states.ERROR ? [responseError(action)] : [];
  const props = {
    destroyStatus: states.NONE, destroyErrors: [], updateStatus: states.NONE, updateErrors: [],
  };
  if (action.isArchival) {
    props.destroyStatus = state;
    props.destroyErrors = errors;
  } else {
    props.updateStatus = state;
    props.updateErrors = errors;
  }

  return props;
};

export default initialState;
